import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useCharacterCreatorChatSession,
  useChatMessages,
  useElevenlabsVoices,
  useGenerateCharacter,
  usePreviewCharacterPrompt,
  useProjects,
  useUpdateCharacter,
  type GenerateCharacterInput,
} from '../hooks/useApi';
import { AssetReferenceInput } from './AssetReferenceInput';
import { ChatPanel } from './ChatPanel';

const STYLE_PRESETS = [
  { value: 'photorealistic', label: '📸 Fotorrealista (default)' },
  { value: 'hyperreal-ue5', label: '🎮 Hyperreal UE5' },
  { value: 'pixar', label: '🎬 Pixar' },
  { value: 'spider-verse', label: '🕷 Spider-Verse' },
  { value: 'among-us', label: '🚀 Among Us' },
  { value: 'semi-realistic-aaa', label: '🎮 Semi-realistic AAA' },
  { value: 'brutalist', label: '🧱 Brutalist' },
  { value: 'fantasy', label: '🧙 Fantasy' },
  { value: 'cyberpunk', label: '🌃 Cyberpunk' },
  { value: 'clean-ui', label: '✨ Clean UI' },
];

/**
 * Wizard creador de personajes (cross-cutting). Genera personaje con prompt A1/A2
 * oficial de Alejo + atributos (edad, género, profesión, nacionalidad, etc.) +
 * opcional referencia visual y logo. Llama a fal-ai/nano-banana-pro/edit (~$0.15).
 *
 * Cuando completa: agrega el personaje a la galería global automáticamente.
 */
export function CharacterFormModal({ onClose }: { onClose: () => void }) {
  // Default 'chat' — el camino conversacional es el principal (decisión 2026-04-28).
  // El wizard tradicional queda como atajo para batch / operadores apurados.
  const [inputMode, setInputMode] = useState<'chat' | 'wizard'>('chat');
  const [chatId, setChatId] = useState<string | null>(null);
  const chatSession = useCharacterCreatorChatSession();

  // Cargar chatId al entrar en modo chat la primera vez. Mutación idempotente:
  // siempre devuelve el mismo chat para el user (persistente entre sesiones).
  useEffect(() => {
    if (inputMode === 'chat' && !chatId && !chatSession.isPending) {
      chatSession
        .mutateAsync()
        .then((r) => setChatId(r.chatId))
        .catch(() => {
          /* error visible en chatSession.error; el operador puede reintentar */
        });
    }
  }, [inputMode, chatId, chatSession]);

  // Escuchar mensajes del chat y aplicar tool_uses de xvision_extract_character_params
  // al form en tiempo real. Trackeamos los tool_use id ya procesados para no re-aplicar.
  const { data: chatMessages } = useChatMessages(chatId ?? undefined);
  const processedToolUseIds = useRef<Set<string>>(new Set());

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState('');
  const [type, setType] = useState<'human' | 'nonhuman'>('human');
  const [mode, setMode] = useState<'with-ref' | 'from-scratch'>('from-scratch');
  const [referenceAssetId, setReferenceAssetId] = useState('');
  const [logoAssetId, setLogoAssetId] = useState('');
  const [style, setStyle] = useState('photorealistic');
  const [description, setDescription] = useState('');

  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [profession, setProfession] = useState('');
  const [nationality, setNationality] = useState('');
  const [skinTone, setSkinTone] = useState('');
  const [hair, setHair] = useState('');
  const [clothing, setClothing] = useState('');
  const [logoHandling, setLogoHandling] = useState<'KEEP' | 'REMOVE' | 'USE PROVIDED LOGO' | 'NONE'>('NONE');
  const [nametag, setNametag] = useState('');
  const [props, setProps] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [voiceManuallySet, setVoiceManuallySet] = useState(false);
  const [projectId, setProjectId] = useState('');

  const { data: voices } = useElevenlabsVoices();
  const { data: projects } = useProjects();
  const generate = useGenerateCharacter();
  const updateCharacter = useUpdateCharacter();
  const previewPrompt = usePreviewCharacterPrompt();
  const [error, setError] = useState<string | null>(null);

  // Live-preview del prompt (paso 3). `promptDraft` arranca con lo que devuelve la
  // API; si el operador lo edita, `promptEdited` pasa a true y se manda como
  // promptOverride al generar. "↻ Regenerar" lo vuelve a armar desde los atributos.
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');
  const [promptEdited, setPromptEdited] = useState(false);

  const fetchPromptPreview = async () => {
    const r = await previewPrompt.mutateAsync({
      name: name.trim(),
      mode,
      type,
      attributes: {
        age: age || undefined,
        gender: gender || undefined,
        profession: profession || undefined,
        nationality: nationality || undefined,
        skinTone: skinTone || undefined,
        hair: hair || undefined,
        clothing: clothing || undefined,
        logoHandling,
        nametag: nametag || undefined,
        props: props || undefined,
      },
      style: style !== 'photorealistic' ? style : undefined,
      description: description || undefined,
    });
    setPromptDraft(r.prompt);
    setPromptEdited(false);
  };

  /**
   * Auto-suggest de voz: cuando el operador escribe nombre/profesión/descripción,
   * busca en las voces clonadas del equipo XLS si alguna matchea por nombre
   * (case-insensitive substring). Si encuentra UNA, la pre-selecciona — solo si
   * el operador no la cambió manualmente. Caso de uso típico: escribir "Greg" en
   * profesión → auto-selecciona la voz "XLS - Greg".
   */
  const suggestedVoice = useMemo(() => {
    if (!voices) return null;
    const haystack = `${name} ${profession} ${description}`.toLowerCase();
    if (haystack.trim().length < 3) return null;
    const clonadas = voices.filter((v) => v.isCloned);
    for (const v of clonadas) {
      // Extraer la palabra "core" del nombre de la voz (ej. "XLS - Greg (M)" → "greg")
      const core = v.name
        .toLowerCase()
        .replace(/^xls\s*-\s*/, '')
        .split(/[\s(]/)[0];
      if (core.length >= 3 && haystack.includes(core)) return v;
    }
    return null;
  }, [voices, name, profession, description]);

  useEffect(() => {
    if (suggestedVoice && !voiceManuallySet && !voiceId) {
      setVoiceId(suggestedVoice.voice_id);
    }
  }, [suggestedVoice, voiceManuallySet, voiceId]);

  /**
   * Wire chat → form: cuando el agente xvision-personaje llama la tool
   * `xvision_extract_character_params`, aplicar los campos al form en tiempo real.
   * Tracking por tool_use.id evita re-aplicar el mismo bloque (los messages se
   * refetchean en cada SSE event).
   */
  useEffect(() => {
    if (!chatMessages) return;
    for (const msg of chatMessages) {
      if (msg.role !== 'assistant') continue;
      const blocks = Array.isArray(msg.content) ? (msg.content as unknown[]) : [];
      for (const block of blocks) {
        if (
          typeof block !== 'object' ||
          block === null ||
          (block as { type?: string }).type !== 'tool_use' ||
          (block as { name?: string }).name !== 'xvision_extract_character_params'
        ) {
          continue;
        }
        const toolUseId = (block as { id?: string }).id;
        if (!toolUseId || processedToolUseIds.current.has(toolUseId)) continue;
        processedToolUseIds.current.add(toolUseId);
        const input = ((block as { input?: Record<string, unknown> }).input ?? {}) as {
          name?: string;
          type?: 'human' | 'nonhuman';
          mode?: 'with-ref' | 'from-scratch';
          style?: string;
          description?: string;
          attributes?: Record<string, string | undefined>;
        };
        if (input.name) setName(input.name);
        if (input.type) setType(input.type);
        if (input.mode) setMode(input.mode);
        if (input.style) setStyle(input.style);
        if (input.description) setDescription(input.description);
        const a = input.attributes ?? {};
        if (a.age) setAge(a.age);
        if (a.gender) setGender(a.gender);
        if (a.profession) setProfession(a.profession);
        if (a.nationality) setNationality(a.nationality);
        if (a.skinTone) setSkinTone(a.skinTone);
        if (a.hair) setHair(a.hair);
        if (a.clothing) setClothing(a.clothing);
        if (a.logoHandling && ['KEEP', 'REMOVE', 'USE PROVIDED LOGO', 'NONE'].includes(a.logoHandling)) {
          setLogoHandling(a.logoHandling as 'KEEP' | 'REMOVE' | 'USE PROVIDED LOGO' | 'NONE');
        }
        if (a.nametag) setNametag(a.nametag);
        if (a.props) setProps(a.props);
      }
    }
  }, [chatMessages]);

  const submit = async () => {
    if (!name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    if (mode === 'with-ref' && !referenceAssetId.trim()) {
      setError('Modo "con referencia" requiere asset_id de la imagen');
      return;
    }
    setError(null);
    const input: GenerateCharacterInput = {
      name: name.trim(),
      mode,
      type,
      referenceAssetId: mode === 'with-ref' ? referenceAssetId.trim() : undefined,
      logoAssetId: logoHandling === 'USE PROVIDED LOGO' ? logoAssetId.trim() || undefined : undefined,
      attributes: {
        age: age || undefined,
        gender: gender || undefined,
        profession: profession || undefined,
        nationality: nationality || undefined,
        skinTone: skinTone || undefined,
        hair: hair || undefined,
        clothing: clothing || undefined,
        logoHandling,
        nametag: nametag || undefined,
        props: props || undefined,
      },
      style: style !== 'photorealistic' ? style : undefined,
      description: description || undefined,
      // Solo si el operador editó el prompt en el live-preview.
      promptOverride: promptEdited && promptDraft.trim() ? promptDraft.trim() : undefined,
    };
    try {
      const result = await generate.mutateAsync(input);
      // Asignar voz y/o proyecto al personaje recién creado.
      if (result.characterId && (voiceId || projectId)) {
        try {
          await updateCharacter.mutateAsync({
            id: result.characterId,
            input: {
              ...(voiceId ? { voiceId, voiceProvider: 'elevenlabs' } : {}),
              ...(projectId ? { projectId } : {}),
            },
          });
        } catch {
          /* tolerante */
        }
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`bg-slate-900 border border-slate-700 rounded-lg w-full flex flex-col max-h-[90vh] ${
          inputMode === 'chat' ? 'max-w-7xl' : 'max-w-2xl'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-slate-100">+ Nuevo personaje</h2>
            <div className="flex rounded border border-slate-700 overflow-hidden">
              <button
                onClick={() => setInputMode('chat')}
                className={`px-3 py-1 text-xs ${
                  inputMode === 'chat'
                    ? 'bg-vision/20 text-vision'
                    : 'bg-slate-950 text-slate-400 hover:text-slate-200'
                }`}
                title="Charlá con el experto y el form se va auto-llenando"
              >
                💬 Chat con experto
              </button>
              <button
                onClick={() => setInputMode('wizard')}
                className={`px-3 py-1 text-xs border-l border-slate-700 ${
                  inputMode === 'wizard'
                    ? 'bg-vision/20 text-vision'
                    : 'bg-slate-950 text-slate-400 hover:text-slate-200'
                }`}
                title="Wizard tradicional con 3 pasos"
              >
                📋 Wizard
              </button>
            </div>
            {inputMode === 'wizard' && (
              <p className="text-[10px] text-slate-500">
                Paso {step} de 3 · ~$0.15 + ~30s
              </p>
            )}
            {inputMode === 'chat' && (
              <p className="text-[10px] text-slate-500">
                xvision-personaje · ~$0.15 al generar
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 text-lg leading-none">
            ✕
          </button>
        </header>

        {inputMode === 'chat' && (
          <div className="grid grid-cols-2 flex-1 overflow-hidden">
            <div className="border-r border-slate-800 overflow-hidden flex flex-col">
              {chatId ? (
                <ChatPanel chatId={chatId} />
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-500 text-xs p-4 text-center">
                  {chatSession.isPending && '🔄 Cargando sesión de chat…'}
                  {chatSession.isError && (
                    <span className="text-red-400">
                      ❌ No se pudo iniciar el chat: {chatSession.error instanceof Error ? chatSession.error.message : 'error desconocido'}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 space-y-4 overflow-y-auto">
              <div className="text-[10px] text-slate-500 uppercase border-b border-slate-800 pb-2 mb-2">
                📋 Form (auto-rellenado por el chat — editable)
              </div>
              <AllFieldsForm
                name={name} setName={setName}
                type={type} setType={setType}
                mode={mode} setMode={setMode}
                referenceAssetId={referenceAssetId} setReferenceAssetId={setReferenceAssetId}
                logoAssetId={logoAssetId} setLogoAssetId={setLogoAssetId}
                style={style} setStyle={setStyle}
                description={description} setDescription={setDescription}
                age={age} setAge={setAge}
                gender={gender} setGender={setGender}
                profession={profession} setProfession={setProfession}
                nationality={nationality} setNationality={setNationality}
                skinTone={skinTone} setSkinTone={setSkinTone}
                hair={hair} setHair={setHair}
                clothing={clothing} setClothing={setClothing}
                logoHandling={logoHandling} setLogoHandling={setLogoHandling}
                nametag={nametag} setNametag={setNametag}
                props={props} setProps={setProps}
                voiceId={voiceId} setVoiceId={setVoiceId}
                setVoiceManuallySet={setVoiceManuallySet}
                projectId={projectId} setProjectId={setProjectId}
                voices={voices ?? []}
                projects={projects ?? []}
                suggestedVoice={suggestedVoice}
                voiceManuallySet={voiceManuallySet}
              />
              <PromptPreviewCollapsible
                show={showPrompt} setShow={setShowPrompt}
                draft={promptDraft} setDraft={setPromptDraft}
                edited={promptEdited} setEdited={setPromptEdited}
                onFetch={() => void fetchPromptPreview()}
                isPending={previewPrompt.isPending}
              />
              {error && (
                <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
                  {error}
                </div>
              )}
              <button
                onClick={submit}
                disabled={generate.isPending || !name.trim()}
                className="w-full px-4 py-2 text-sm rounded bg-vision-dark hover:bg-vision text-white disabled:opacity-40"
              >
                {generate.isPending ? '🎨 Generando (~30s)…' : '🎨 Generar (~$0.15)'}
              </button>
            </div>
          </div>
        )}

        {inputMode === 'wizard' && (
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {step === 1 && (
            <>
              <div>
                <label className="text-xs text-slate-400 uppercase mb-1 block">Nombre del personaje *</label>
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="ej: Dr. Soto, Fluxito, Asistente Greta"
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-vision"
                />
              </div>

              <div>
                <label className="text-xs text-slate-400 uppercase mb-1 block">Tipo</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setType('human')}
                    className={`p-3 rounded border text-sm ${
                      type === 'human' ? 'border-vision bg-vision/10 text-slate-100' : 'border-slate-700 bg-slate-950 text-slate-400'
                    }`}
                  >
                    🧑 Humano (A1)
                  </button>
                  <button
                    onClick={() => setType('nonhuman')}
                    className={`p-3 rounded border text-sm ${
                      type === 'nonhuman' ? 'border-vision bg-vision/10 text-slate-100' : 'border-slate-700 bg-slate-950 text-slate-400'
                    }`}
                  >
                    🤖 No humano (robot/mascota)
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 uppercase mb-1 block">Modo</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setMode('from-scratch')}
                    className={`p-3 rounded border text-sm text-left ${
                      mode === 'from-scratch' ? 'border-vision bg-vision/10 text-slate-100' : 'border-slate-700 bg-slate-950 text-slate-400'
                    }`}
                  >
                    ✨ Desde cero
                    <div className="text-[10px] text-slate-500 mt-0.5">Definís todos los atributos</div>
                  </button>
                  <button
                    onClick={() => setMode('with-ref')}
                    className={`p-3 rounded border text-sm text-left ${
                      mode === 'with-ref' ? 'border-vision bg-vision/10 text-slate-100' : 'border-slate-700 bg-slate-950 text-slate-400'
                    }`}
                  >
                    📸 Con referencia
                    <div className="text-[10px] text-slate-500 mt-0.5">Foto del cliente / persona real</div>
                  </button>
                </div>
              </div>

              {mode === 'with-ref' && (
                <AssetReferenceInput
                  value={referenceAssetId}
                  onChange={setReferenceAssetId}
                  label="Foto de referencia *"
                />
              )}
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="text-xs text-slate-400 uppercase">Atributos</h3>
              <div className="grid grid-cols-2 gap-3">
                {type === 'human' && (
                  <>
                    <Field label="Edad" value={age} onChange={setAge} placeholder="Young Adult (20-30)" />
                    <Field label="Género" value={gender} onChange={setGender} placeholder="Female / Male / Non-binary" />
                    <Field label="Profesión / rol" value={profession} onChange={setProfession} placeholder="Médico / Mecánico / Maestro" />
                    <Field label="Nacionalidad / etnia" value={nationality} onChange={setNationality} placeholder="Argentine / Latin American" />
                    <Field label="Tono de piel" value={skinTone} onChange={setSkinTone} placeholder="Light / Medium / Dark" />
                    <Field label="Pelo" value={hair} onChange={setHair} placeholder="Black short / Brown long / Bald" />
                  </>
                )}
                {type === 'nonhuman' && (
                  <>
                    <Field label="Tipo / rol" value={profession} onChange={setProfession} placeholder="Service robot / Mascot / Creature" />
                    <Field label="Materiales / look" value={clothing} onChange={setClothing} placeholder="White metal with blue accents" />
                  </>
                )}
                {type === 'human' && (
                  <Field label="Vestimenta" value={clothing} onChange={setClothing} placeholder="Lab coat / Casual / Uniform" className="col-span-2" />
                )}
              </div>

              <div>
                <label className="text-xs text-slate-400 uppercase mb-1 block">Estilo visual</label>
                <select
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
                >
                  {STYLE_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h3 className="text-xs text-slate-400 uppercase">Branding y extras</h3>

              <div>
                <label className="text-xs text-slate-400 uppercase mb-1 block">Manejo de logo</label>
                <select
                  value={logoHandling}
                  onChange={(e) => setLogoHandling(e.target.value as typeof logoHandling)}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
                >
                  <option value="NONE">Sin logo</option>
                  <option value="KEEP">Mantener logo de la referencia</option>
                  <option value="REMOVE">Eliminar logos visibles</option>
                  <option value="USE PROVIDED LOGO">Usar logo provisto (asset ID abajo)</option>
                </select>
              </div>

              {logoHandling === 'USE PROVIDED LOGO' && (
                <AssetReferenceInput
                  value={logoAssetId}
                  onChange={setLogoAssetId}
                  label="Logo del cliente (PNG)"
                />
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Nametag" value={nametag} onChange={setNametag} placeholder="EMPTY / [nombre] / NONE" />
                <Field label="Props sostenidos" value={props} onChange={setProps} placeholder="Tablet / wrench / NONE" />
              </div>

              <div>
                <label className="text-xs text-slate-400 uppercase mb-1 block">
                  🎤 Asignar voz (opcional)
                </label>
                {suggestedVoice && voiceId === suggestedVoice.voice_id && !voiceManuallySet && (
                  <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 mb-1 flex items-center justify-between gap-2">
                    <span>
                      💡 Auto-sugerido por nombre: <strong>{suggestedVoice.name}</strong>
                    </span>
                    <button
                      onClick={() => {
                        setVoiceId('');
                        setVoiceManuallySet(true);
                      }}
                      className="text-[10px] text-slate-400 hover:text-slate-200 underline"
                    >
                      no, otra
                    </button>
                  </div>
                )}
                <select
                  value={voiceId}
                  onChange={(e) => {
                    setVoiceId(e.target.value);
                    setVoiceManuallySet(true);
                  }}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
                >
                  <option value="">— sin voz (asignar después) —</option>
                  <optgroup label="🎭 Voces clonadas / profesionales (XLS)">
                    {voices?.filter((v) => v.isCloned).map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>
                        {v.name} {v.category ? `[${v.category}]` : ''}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="📚 Voces públicas (catálogo ElevenLabs)">
                    {voices?.filter((v) => !v.isCloned).map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>
                        {v.name} {v.category ? `[${v.category}]` : ''}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {voiceId && (
                  <div className="text-[10px] text-emerald-400/80 mt-1">
                    ✓ Voz asignada al crear — el personaje queda listo para lipsync.
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs text-slate-400 uppercase mb-1 block">
                  📁 Asignar a proyecto (opcional)
                </label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
                >
                  <option value="">🌐 Galería global (default — todos los proyectos)</option>
                  {projects?.map((p) => (
                    <option key={p.id} value={p.id}>
                      📁 {p.name}{p.client ? ` · ${p.client}` : ''}
                    </option>
                  ))}
                </select>
                <div className="text-[10px] text-slate-500 mt-1">
                  Default: galería global (visible en todos los proyectos). Asignar a un proyecto
                  específico lo hace exclusivo.
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-400 uppercase mb-1 block">Notas adicionales</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-vision resize-none"
                  placeholder="Detalles extra para el modelo..."
                />
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded p-3 text-xs text-slate-400 space-y-1">
                <div>📋 <strong>Resumen</strong>:</div>
                <div>• Nombre: {name || '(sin nombre)'}</div>
                <div>• {type === 'human' ? 'Humano (A1)' : 'No humano (A2)'} — {mode === 'with-ref' ? 'con referencia' : 'desde cero'}</div>
                <div>• Estilo: {STYLE_PRESETS.find((p) => p.value === style)?.label ?? style}</div>
                <div className="text-amber-400">💰 ~$0.15 · ⏱ ~30 segundos</div>
              </div>

              <PromptPreviewCollapsible
                show={showPrompt} setShow={setShowPrompt}
                draft={promptDraft} setDraft={setPromptDraft}
                edited={promptEdited} setEdited={setPromptEdited}
                onFetch={() => void fetchPromptPreview()}
                isPending={previewPrompt.isPending}
              />
            </>
          )}

          {error && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
              {error}
            </div>
          )}
        </div>
        )}

        {inputMode === 'wizard' && (
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-slate-800 shrink-0">
          {step > 1 ? (
            <button
              onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)}
              className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200"
            >
              ← atrás
            </button>
          ) : (
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200">
              Cancelar
            </button>
          )}
          {step < 3 ? (
            <button
              onClick={() => setStep((s) => (s + 1) as 1 | 2 | 3)}
              disabled={step === 1 && !name.trim()}
              className="px-4 py-2 text-sm rounded bg-vision-dark hover:bg-vision text-white disabled:opacity-40"
            >
              Siguiente →
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={generate.isPending || !name.trim()}
              className="px-4 py-2 text-sm rounded bg-vision-dark hover:bg-vision text-white disabled:opacity-40"
            >
              {generate.isPending ? '🎨 Generando (~30s)…' : '🎨 Generar (~$0.15)'}
            </button>
          )}
        </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Collapsible "🔍 Ver prompt que se va a usar" — live-preview del prompt exacto
 * antes de gastar los $0.15. Se usa tanto en el paso 3 del wizard como en el panel
 * del modo chat. Si el operador edita, se manda como promptOverride al generar.
 */
function PromptPreviewCollapsible({
  show, setShow, draft, setDraft, edited, setEdited, onFetch, isPending,
}: {
  show: boolean;
  setShow: (v: boolean) => void;
  draft: string;
  setDraft: (v: string) => void;
  edited: boolean;
  setEdited: (v: boolean) => void;
  onFetch: () => void;
  isPending: boolean;
}) {
  return (
    <div className="border border-slate-800 rounded">
      <button
        type="button"
        onClick={() => {
          const next = !show;
          setShow(next);
          if (next && !draft) onFetch();
        }}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-300 hover:text-slate-100"
      >
        <span>🔍 Ver prompt que se va a usar {edited && <span className="text-vision">(editado)</span>}</span>
        <span className="text-slate-500">{show ? '▾' : '▸'}</span>
      </button>
      {show && (
        <div className="px-3 pb-3 space-y-2">
          {isPending && !draft ? (
            <div className="text-xs text-slate-500">Armando prompt…</div>
          ) : (
            <>
              <textarea
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setEdited(true);
                }}
                rows={10}
                spellCheck={false}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-[11px] font-mono leading-relaxed text-slate-200 focus:outline-none focus:border-vision resize-y"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">
                  {edited
                    ? 'Se enviará este texto editado tal cual.'
                    : 'Se arma desde los atributos. Editalo si querés ajustarlo.'}
                </span>
                <button
                  type="button"
                  onClick={onFetch}
                  disabled={isPending}
                  className="text-[10px] px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"
                >
                  ↻ Regenerar desde atributos
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="text-xs text-slate-400 uppercase mb-1 block">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-vision"
      />
    </div>
  );
}

interface AllFieldsFormProps {
  name: string; setName: (v: string) => void;
  type: 'human' | 'nonhuman'; setType: (v: 'human' | 'nonhuman') => void;
  mode: 'with-ref' | 'from-scratch'; setMode: (v: 'with-ref' | 'from-scratch') => void;
  referenceAssetId: string; setReferenceAssetId: (v: string) => void;
  logoAssetId: string; setLogoAssetId: (v: string) => void;
  style: string; setStyle: (v: string) => void;
  description: string; setDescription: (v: string) => void;
  age: string; setAge: (v: string) => void;
  gender: string; setGender: (v: string) => void;
  profession: string; setProfession: (v: string) => void;
  nationality: string; setNationality: (v: string) => void;
  skinTone: string; setSkinTone: (v: string) => void;
  hair: string; setHair: (v: string) => void;
  clothing: string; setClothing: (v: string) => void;
  logoHandling: 'KEEP' | 'REMOVE' | 'USE PROVIDED LOGO' | 'NONE'; setLogoHandling: (v: 'KEEP' | 'REMOVE' | 'USE PROVIDED LOGO' | 'NONE') => void;
  nametag: string; setNametag: (v: string) => void;
  props: string; setProps: (v: string) => void;
  voiceId: string; setVoiceId: (v: string) => void;
  setVoiceManuallySet: (v: boolean) => void;
  projectId: string; setProjectId: (v: string) => void;
  voices: Array<{ voice_id: string; name: string; isCloned: boolean; category?: string | null }>;
  projects: Array<{ id: string; name: string; client?: { name: string } | null }>;
  suggestedVoice: { voice_id: string; name: string } | null;
  voiceManuallySet: boolean;
}

/**
 * Render compacto de TODOS los campos del wizard apilados sin steps. Usado en modo
 * chat para que el operador vea cómo el agente va llenando los campos en tiempo real.
 */
function AllFieldsForm(p: AllFieldsFormProps) {
  return (
    <>
      <div>
        <label className="text-xs text-slate-400 uppercase mb-1 block">Nombre *</label>
        <input
          value={p.name}
          onChange={(e) => p.setName(e.target.value)}
          placeholder="ej: Dr. Soto"
          className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-vision"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-slate-500 uppercase mb-1 block">Tipo</label>
          <select
            value={p.type}
            onChange={(e) => p.setType(e.target.value as 'human' | 'nonhuman')}
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-100"
          >
            <option value="human">🧑 Humano (A1)</option>
            <option value="nonhuman">🤖 No humano (A2)</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase mb-1 block">Modo</label>
          <select
            value={p.mode}
            onChange={(e) => p.setMode(e.target.value as 'with-ref' | 'from-scratch')}
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-100"
          >
            <option value="from-scratch">✨ Desde cero</option>
            <option value="with-ref">📸 Con referencia</option>
          </select>
        </div>
      </div>

      {p.mode === 'with-ref' && (
        <AssetReferenceInput
          value={p.referenceAssetId}
          onChange={p.setReferenceAssetId}
          label="Foto de referencia *"
        />
      )}

      <div className="grid grid-cols-2 gap-2">
        {p.type === 'human' && (
          <>
            <Field label="Edad" value={p.age} onChange={p.setAge} placeholder="30-40" />
            <Field label="Género" value={p.gender} onChange={p.setGender} placeholder="Female" />
            <Field label="Profesión" value={p.profession} onChange={p.setProfession} placeholder="Médico" />
            <Field label="Nacionalidad" value={p.nationality} onChange={p.setNationality} placeholder="Argentine" />
            <Field label="Piel" value={p.skinTone} onChange={p.setSkinTone} placeholder="Light" />
            <Field label="Pelo" value={p.hair} onChange={p.setHair} placeholder="Black short" />
          </>
        )}
        {p.type === 'nonhuman' && (
          <>
            <Field label="Tipo / rol" value={p.profession} onChange={p.setProfession} placeholder="Service robot" />
            <Field label="Materiales / look" value={p.clothing} onChange={p.setClothing} placeholder="White metal" />
          </>
        )}
        {p.type === 'human' && (
          <Field label="Vestimenta" value={p.clothing} onChange={p.setClothing} placeholder="Lab coat" className="col-span-2" />
        )}
      </div>

      <div>
        <label className="text-xs text-slate-400 uppercase mb-1 block">Estilo</label>
        <select
          value={p.style}
          onChange={(e) => p.setStyle(e.target.value)}
          className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
        >
          {STYLE_PRESETS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs text-slate-400 uppercase mb-1 block">Logo</label>
        <select
          value={p.logoHandling}
          onChange={(e) => p.setLogoHandling(e.target.value as 'KEEP' | 'REMOVE' | 'USE PROVIDED LOGO' | 'NONE')}
          className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
        >
          <option value="NONE">Sin logo</option>
          <option value="KEEP">Mantener</option>
          <option value="REMOVE">Eliminar</option>
          <option value="USE PROVIDED LOGO">Usar logo provisto</option>
        </select>
      </div>

      {p.logoHandling === 'USE PROVIDED LOGO' && (
        <AssetReferenceInput
          value={p.logoAssetId}
          onChange={p.setLogoAssetId}
          label="Logo del cliente"
        />
      )}

      <div className="grid grid-cols-2 gap-2">
        <Field label="Nametag" value={p.nametag} onChange={p.setNametag} placeholder="EMPTY / [nombre]" />
        <Field label="Props" value={p.props} onChange={p.setProps} placeholder="Tablet / NONE" />
      </div>

      <div>
        <label className="text-xs text-slate-400 uppercase mb-1 block">🎤 Voz</label>
        {p.suggestedVoice && p.voiceId === p.suggestedVoice.voice_id && !p.voiceManuallySet && (
          <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 mb-1">
            💡 Auto-sugerida: <strong>{p.suggestedVoice.name}</strong>
          </div>
        )}
        <select
          value={p.voiceId}
          onChange={(e) => {
            p.setVoiceId(e.target.value);
            p.setVoiceManuallySet(true);
          }}
          className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
        >
          <option value="">— sin voz —</option>
          <optgroup label="🎭 Clonadas (XLS)">
            {p.voices.filter((v) => v.isCloned).map((v) => (
              <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
            ))}
          </optgroup>
          <optgroup label="📚 Públicas">
            {p.voices.filter((v) => !v.isCloned).map((v) => (
              <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
            ))}
          </optgroup>
        </select>
      </div>

      <div>
        <label className="text-xs text-slate-400 uppercase mb-1 block">📁 Proyecto</label>
        <select
          value={p.projectId}
          onChange={(e) => p.setProjectId(e.target.value)}
          className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
        >
          <option value="">🌐 Galería global</option>
          {p.projects.map((pr) => (
            <option key={pr.id} value={pr.id}>📁 {pr.name}{pr.client ? ` · ${pr.client}` : ''}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs text-slate-400 uppercase mb-1 block">Notas</label>
        <textarea
          value={p.description}
          onChange={(e) => p.setDescription(e.target.value)}
          rows={2}
          className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-vision resize-none"
          placeholder="Detalles extra..."
        />
      </div>
    </>
  );
}
